// PdfReader — premium reading surface backed by PDF.js.
//
// Hardened for production:
//  • Pages turn via stateful pageNumber (no iframe reload).
//  • Tap-left / tap-right zones for mobile page flipping.
//  • Swipe gestures for the same.
//  • Keyboard ← → and Space.
//  • Smooth page-turn fade.
//  • Auto-fits the container width on every resize; cap at 980px.
//  • Zoom controls live at the bottom-right, out of the reading line.
//  • Anti-save (text/annotation layers disabled, no right-click).
//  • Defensive: if the worker file ever 404s, surfaces a clear message
//    rather than a cryptic "fake worker" failure.
//
import { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import { Loader2, ZoomIn, ZoomOut } from "lucide-react";
import "react-pdf/dist/esm/Page/AnnotationLayer.css";
import "react-pdf/dist/esm/Page/TextLayer.css";

// Load the PDF.js worker from our own public folder.
// IMPORTANT: We use the `.js` extension (not `.mjs`) because some static
// hosts / CDNs return `application/octet-stream` for `.mjs` files, which
// browsers reject as JavaScript. The file contents are still an ES module
// — pdfjs spawns the worker with `{ type: 'module' }` regardless of the
// URL extension, so this is fully spec-compliant.
pdfjs.GlobalWorkerOptions.workerSrc = "/pdfjs/pdf.worker.min.js";

export default function PdfReader({
  fileUrl,                    // blob: URL or remote URL
  currentPage,                // 1-indexed
  onPageChange,               // (next: number) => void
  onTotalPages,               // (total: number) => void
  totalPages,                 // hint from meta if known
  isLightMode,
}) {
  const [scale, setScale] = useState(1.0);
  const [width, setWidth] = useState(0);
  const [docOK, setDocOK] = useState(false);
  const [docErr, setDocErr] = useState(null);
  const [flipping, setFlipping] = useState(false);
  const [loadProgress, setLoadProgress] = useState(0); // 0..1
  const containerRef = useRef(null);
  const lastPageRef = useRef(currentPage);

  // Compute the page width based on available container space.
  // Subtract a margin so we never overflow on mobile.
  useEffect(() => {
    if (!containerRef.current) return;
    const compute = () => {
      const w = containerRef.current?.clientWidth || 0;
      // Reserve 24px gutter on mobile, 48px on larger so zoom panel never overlaps content.
      const gutter = w < 640 ? 24 : 48;
      const capped = Math.min(w - gutter, 980);
      setWidth(Math.max(280, capped));
    };
    compute();
    const ro = new ResizeObserver(compute);
    ro.observe(containerRef.current);
    window.addEventListener("orientationchange", compute);
    return () => {
      ro.disconnect();
      window.removeEventListener("orientationchange", compute);
    };
  }, []);

  // Smooth fade between pages
  useEffect(() => {
    if (lastPageRef.current !== currentPage) {
      setFlipping(true);
      lastPageRef.current = currentPage;
      const t = setTimeout(() => setFlipping(false), 180);
      // Snap scroll back to the top of the page when flipping
      if (containerRef.current) containerRef.current.scrollTo({ top: 0, behavior: "instant" });
      return () => clearTimeout(t);
    }
  }, [currentPage]);

  // Keyboard ← → / Space for page nav
  useEffect(() => {
    const onKey = (e) => {
      if (e.target?.tagName === "INPUT" || e.target?.tagName === "TEXTAREA") return;
      if (e.key === "ArrowRight" || e.key === " " || e.key === "PageDown") {
        e.preventDefault();
        onPageChange(currentPage + 1);
      } else if (e.key === "ArrowLeft" || e.key === "PageUp") {
        e.preventDefault();
        onPageChange(currentPage - 1);
      } else if ((e.key === "+" || e.key === "=") && (e.ctrlKey || e.metaKey)) {
        e.preventDefault();
        setScale((s) => Math.min(2.4, +(s + 0.15).toFixed(2)));
      } else if (e.key === "-" && (e.ctrlKey || e.metaKey)) {
        e.preventDefault();
        setScale((s) => Math.max(0.5, +(s - 0.15).toFixed(2)));
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [currentPage, onPageChange]);

  // Touch swipe — flick left/right to flip the page on phones
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    let startX = 0, startY = 0, startedAt = 0;
    const onStart = (e) => {
      const t = e.touches?.[0];
      if (!t) return;
      startX = t.clientX; startY = t.clientY; startedAt = Date.now();
    };
    const onEnd = (e) => {
      const t = e.changedTouches?.[0];
      if (!t) return;
      const dx = t.clientX - startX, dy = t.clientY - startY;
      const dt = Date.now() - startedAt;
      // Only treat as a horizontal swipe if it's clearly horizontal & fast
      if (Math.abs(dx) > 60 && Math.abs(dx) > Math.abs(dy) * 1.6 && dt < 600) {
        if (dx < 0) onPageChange(currentPage + 1);
        else onPageChange(currentPage - 1);
      }
    };
    el.addEventListener("touchstart", onStart, { passive: true });
    el.addEventListener("touchend", onEnd, { passive: true });
    return () => {
      el.removeEventListener("touchstart", onStart);
      el.removeEventListener("touchend", onEnd);
    };
  }, [currentPage, onPageChange]);

  const onDocLoad = useCallback((doc) => {
    setDocOK(true);
    setDocErr(null);
    onTotalPages?.(doc.numPages);
  }, [onTotalPages]);

  const onDocErr = useCallback((err) => {
    const msg = err?.message || "PDF could not be opened.";
    // Provide a clearer message when the worker fails to load
    if (/worker|MIME/i.test(msg)) {
      setDocErr("Couldn't start the page renderer. Please hard-refresh the page (Cmd/Ctrl+Shift+R).");
    } else {
      setDocErr(msg);
    }
    setDocOK(false);
  }, []);

  const inc = () => setScale((s) => Math.min(2.4, +(s + 0.15).toFixed(2)));
  const dec = () => setScale((s) => Math.max(0.5, +(s - 0.15).toFixed(2)));
  const resetZoom = () => setScale(1.0);

  // Memoize the options prop so react-pdf doesn't reload the PDF on every render.
  const docOptions = useMemo(() => ({
    disableAutoFetch: false,
    disableStream: false,
    cMapUrl: `https://unpkg.com/pdfjs-dist@${pdfjs.version}/cmaps/`,
    cMapPacked: true,
    standardFontDataUrl: `https://unpkg.com/pdfjs-dist@${pdfjs.version}/standard_fonts/`,
  }), []);

  const pageNumber = Math.max(1, Math.min(currentPage, totalPages || 999));
  const canPrev = currentPage > 1;
  const canNext = !totalPages || currentPage < totalPages;

  return (
    <div
      ref={containerRef}
      className="w-full h-full overflow-auto flex flex-col items-center pdf-reader-scroll relative"
      style={{
        background: isLightMode ? "#EFE8DA" : "#0d0d0d",
        scrollbarWidth: "thin",
        scrollbarColor: isLightMode ? "rgba(0,0,0,0.2) transparent" : "rgba(255,255,255,0.15) transparent",
      }}
      data-testid="pdf-reader"
    >
      {!docOK && !docErr && (
        <div className="m-auto flex flex-col items-center gap-3">
          <div className="flex items-center gap-3 overline opacity-70">
            <Loader2 className="h-4 w-4 animate-spin" />
            {loadProgress > 0 && loadProgress < 1
              ? `Loading edition · ${Math.round(loadProgress * 100)}%`
              : "Rendering page…"}
          </div>
          {loadProgress > 0 && loadProgress < 1 && (
            <div className="w-48 h-0.5 bg-white/10 overflow-hidden rounded-full">
              <div
                className="h-full bg-current transition-all duration-300"
                style={{ width: `${Math.round(loadProgress * 100)}%` }}
              />
            </div>
          )}
        </div>
      )}
      {docErr && (
        <div className="m-auto max-w-md text-center px-6">
          <p className="font-serif italic text-lg opacity-80 mb-3">{docErr}</p>
          <p className="text-[12px] opacity-60">
            The file is safe on your shelf. Try reloading; if it persists, sign out and back in.
          </p>
        </div>
      )}

      <div
        className="py-6 sm:py-10 select-none pointer-events-auto transition-opacity duration-200"
        style={{ opacity: flipping ? 0.35 : 1 }}
      >
        <Document
          file={fileUrl}
          onLoadSuccess={onDocLoad}
          onLoadError={onDocErr}
          onLoadProgress={({ loaded, total }) => {
            if (total > 0) setLoadProgress(Math.min(0.99, loaded / total));
          }}
          loading={null}
          options={docOptions}
        >
          {docOK && (
            <Page
              key={`p_${pageNumber}_${scale}_${width}`}
              pageNumber={pageNumber}
              width={width ? width * scale : undefined}
              renderTextLayer={false}     // disable selection so users can't copy/paste the PDF text
              renderAnnotationLayer={false}
              onContextMenu={(e) => e.preventDefault()}
              className="pdf-page-shadow"
            />
          )}
        </Document>
      </div>

      {/* Tap zones — left half = previous, right half = next.
          Sits BELOW the page (z-0) so the page receives pointer-events first,
          and the zones only catch margin clicks. Hidden on coarse pointer
          devices via media query so desktop users don't accidentally flip. */}
      {docOK && (
        <>
          <button
            aria-label="Previous page"
            data-testid="pdf-tap-prev"
            onClick={(e) => { e.preventDefault(); if (canPrev) onPageChange(currentPage - 1); }}
            disabled={!canPrev}
            className="pdf-tap-zone pdf-tap-zone-left"
            tabIndex={-1}
          />
          <button
            aria-label="Next page"
            data-testid="pdf-tap-next"
            onClick={(e) => { e.preventDefault(); if (canNext) onPageChange(currentPage + 1); }}
            disabled={!canNext}
            className="pdf-tap-zone pdf-tap-zone-right"
            tabIndex={-1}
          />
        </>
      )}

      {/* Floating zoom controls — bottom-right of the reader surface so
          they never overlap the page content or the bottom navigator. */}
      {docOK && (
        <div className="absolute right-3 bottom-20 sm:bottom-24 z-30 flex flex-col gap-1 bg-black/65 backdrop-blur-md border border-white/10 rounded-full px-1 py-2 text-white shadow-lg">
          <button
            onClick={inc}
            className="w-9 h-9 grid place-items-center hover:bg-white/15 rounded-full"
            aria-label="Zoom in"
            data-testid="pdf-zoom-in"
          >
            <ZoomIn className="h-4 w-4" />
          </button>
          <button
            onClick={resetZoom}
            className="text-[9px] tracking-[0.18em] uppercase opacity-70 text-center tabular-nums hover:opacity-100"
            aria-label="Reset zoom"
            data-testid="pdf-zoom-reset"
          >
            {Math.round(scale * 100)}%
          </button>
          <button
            onClick={dec}
            className="w-9 h-9 grid place-items-center hover:bg-white/15 rounded-full"
            aria-label="Zoom out"
            data-testid="pdf-zoom-out"
          >
            <ZoomOut className="h-4 w-4" />
          </button>
        </div>
      )}
    </div>
  );
}
